import type { App } from '@grafikr/components';
import header from 'layout/header';

import hasUrlParam from 'utils/loaders/has-url-param';
import intersectionLoader from 'utils/loaders/intersection';
import onElementInteraction from 'utils/loaders/on-element-interaction';
import onEvent from 'utils/loaders/on-event';

export default (app: App) => {
  app.add({
    // Layout
    'announcement-bar': [
      intersectionLoader,
      () => import(/* webpackChunkName: "layout-announcement-bar" */ 'layout/announcement-bar'),
    ],
    'cart-click-and-collect': [
      intersectionLoader,
      () => import(/* webpackChunkName: "layout-cart-click-and-collect" */ 'layout/cart/cart-click-and-collect'),
    ],
    'cart-contents': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-cart-contents" */ 'layout/cart/cart-contents'),
    ],
    'cart-count': [
      [intersectionLoader, onEvent],
      () => import(/* webpackChunkName: "layout-cart-count" */ 'layout/cart-count'),
    ],
    'cart-delivery-conflict-drawer': [
      onEvent,
      () => import(/* webpackChunkName: "layout-cart-delivery-conflict-drawer" */ 'layout/cart/cart-delivery-conflict-drawer'),
    ],
    'cart-drawer': [
      [onEvent, onElementInteraction, hasUrlParam],
      () => import(/* webpackChunkName: "component-cart-drawer" */ 'layout/cart/cart-drawer'),
    ],
    'cart-free-shipping-progress': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-cart-free-shipping-progress" */ 'layout/cart/cart-free-shipping-progress'
        ),
    ],
    'cart-gift-wrapping': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-cart-gift-wrapping" */ 'layout/cart/cart-gift-wrapping'
        ),
    ],
    'cart-item': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-cart-item" */ 'layout/cart/cart-item'),
    ],
    'cart-note': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-cart-note" */ 'layout/cart/cart-note'),
    ],
    'cart-recommendations': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-cart-recommendations" */ 'layout/cart/cart-recommendations'
        ),
    ],
    'cart-recommendations-item': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-cart-recommendations-item" */ 'layout/cart/cart-recommendations-item'
        ),
    ],
    'cart-shipping-cost': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-cart-shipping-cost" */ 'layout/cart/cart-shipping-cost'),
    ],
    'cart-shipping-info': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-cart-shipping-info" */ 'layout/cart/cart-shipping-info'),
    ],
    'cart-split-shipping-info': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-cart-split-shipping-info" */ 'layout/cart/cart-split-shipping-info'),
    ],
    header,
    navigation: [
      intersectionLoader,
      () => import(/* webpackChunkName: "layout-navigation" */ 'layout/navigation'),
    ],
    'navigation-drawer': [
      onElementInteraction,
      () => import(/* webpackChunkName: "layout-navigation-drawer" */ 'layout/navigation-drawer'),
    ],
    'click-and-collect-drawer': [
      [onElementInteraction, onEvent],
      () => import(/* webpackChunkName: "layout-click-and-collect-drawer" */ 'layout/click-and-collect-drawer'),
    ],

    // Components
    accordion: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-accordion" */ 'components/accordion'),
    ],
    'address-form': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-address-form" */ 'components/address-form'),
    ],
    carousel: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-carousel" */ 'components/carousel'),
    ],
    'click-and-collect-store': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-click-and-collect-store" */ 'components/click-and-collect-store'),
    ],
    'click-and-collect-store-selection': [
      intersectionLoader,
      () => import(/* webpackChunkName: "layout-click-and-collect-store-selection" */ 'components/click-and-collect-store-selection'),
    ],
    copy: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-copy" */ 'components/copy'),
    ],
    'lazy-content': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-lazy-content" */ 'components/lazy-content'),
    ],
    'live-search': [
      onElementInteraction,
      () => import(/* webpackChunkName: "component-live-search" */ 'layout/live-search'),
    ],
    pagination: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-pagination" */ 'components/pagination'),
    ],
    'placeholder-image': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-placeholder-image" */ 'components/placeholder-image'
        ),
    ],
    'price-slider': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-price-slider" */ 'components/price-slider'),
    ],
    recommendations: [
      intersectionLoader,
      () =>
        import(/* webpackChunkName: "component-recommendations" */ 'components/recommendations'),
    ],
    video: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-video" */ 'components/video'),
    ],

    // Pages
    collection: [
      intersectionLoader,
      () => import(/* webpackChunkName: "collection-main" */ 'pages/collection/main'),
    ],

    'collection-filter': [
      onElementInteraction,
      () => import(/* webpackChunkName: "collection-filter" */ 'pages/collection/filter'),
    ],
    product: [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-main" */ 'pages/product/main'),
    ],
    'product-back-in-stock-form': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "product-back-in-stock-form" */ 'pages/product/back-in-stock/form'
        ),
    ],
    'product-back-in-stock-popover': [
      onElementInteraction,
      () =>
        import(
          /* webpackChunkName: "product-back-in-stock-popover" */ 'pages/product/back-in-stock/popover'
        ),
    ],
    'product-delivery': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-product-delivery" */ 'pages/product/delivery'),
    ],
    'product-form': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-form" */ 'pages/product/form'),
    ],
    'product-sticky-form-buttons': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "product-sticky-form-buttons" */ 'pages/product/sticky-form-buttons'
        ),
    ],
    'collection-sticky-filter-button': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "collection-sticky-filter-button" */ 'pages/collection/sticky-filter-button'
        ),
    ],

    'search-page': [
      intersectionLoader,
      () => import(/* webpackChunkName: "search-main" */ 'pages/search/main'),
    ],
  });
};
